import React, { useEffect, useState, useRef } from "react";
import "./App.css";
// import Signin from "./Pages/Signin/Signin";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Router from "./Routes/Router";
import { useKeycloak } from "@react-keycloak/web";
import { useDispatch } from "react-redux";
import { fetchProfileData } from "./CentralStore/ExportLMSTable/dataSlice";
import Chatbot from "./Pages/DMS/Chatbot/Chatbot";
import axios from 'axios';

// API base URL - change for production
const API_BASE_URL = "https://demo.iresponse.co.in/cb_api";

// Create axios instance with authentication
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor: Add JWT token to every request
apiClient.interceptors.request.use(
  (config) => {
    // Get token from localStorage (Keycloak token)
    const token = localStorage.getItem('auth_token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔑 Sending request with auth token');
    } else {
      console.warn('⚠️ No auth token found in localStorage');
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle token expiration
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('❌ Authentication failed - Token expired or invalid');
    }
    return Promise.reject(error);
  }
);

function App() {
  // const token = localStorage.getItem("token");
  const { keycloak } = useKeycloak();
  const dispatch = useDispatch();

  // Chatbot state management
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [messages, setMessages] = useState([
    { sender: 'bot', text: 'Welcome to iResponse! How can I assist you?' }
  ]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestion, setLoadingSuggestion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Navigation context state for chatbot
  const [navigationContext, setNavigationContext] = useState({
    level: 'main',
    categoryId: null,
    categoryTitle: null
  });

  const chatRef = useRef(null);

  useEffect(() => {
    if (keycloak.authenticated) {
      // Access the token from Keycloak
      const token = keycloak.token;
      localStorage.setItem("accessToken", token);
      dispatch(fetchProfileData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.authenticated]);

  // Load static categories on mount (NEW: using axios with auth)
  useEffect(() => {
    apiClient.get('/suggestions/static_categories')
      .then(response => setSuggestions(response.data.suggestions || []))
      .catch(error => {
        console.error('Error loading suggestions:', error);
        setSuggestions([]);
      });
  }, []);

  // Re-fetch profile-aware suggestions when chat is first opened
  // (token guaranteed available by then — host app login complete)
  useEffect(() => {
    if (!visible) return;
    apiClient.get('/suggestions/static_categories')
      .then(response => setSuggestions(response.data.suggestions || []))
      .catch(error => console.error('Error re-fetching suggestions on open:', error));
  }, [visible]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // Expose resetChat globally for Chatbot component
  useEffect(() => {
    window.resetChat = resetChat;
    return () => {
      delete window.resetChat;
    };
  }, []);

  // Toggle chatbot visibility
  const toggleChat = () => {
    console.log('🔍 Toggle chat clicked. Current state:', { visible, shouldRender });

    if (visible) {
      setVisible(false);
      setTimeout(() => setShouldRender(false), 300);
    } else {
      setShouldRender(true);
      setTimeout(() => setVisible(true), 10);
    }

    // Debug log after state changes
    setTimeout(() => {
      console.log('🔍 After toggle:', { visible, shouldRender });
    }, 50);
  };

  // Toggle suggestions visibility
  const toggleSuggestions = () => setShowSuggestions(prev => !prev);

  // Reset chat function - Abandons current task on backend
  const resetChat = async () => {
    // Step 1: Get current session ID from Chatbot component
    const stored = localStorage.getItem('iresponse_chatbot_session');
    let sessionId = null;
    
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        sessionId = parsed.id;
      } catch (e) {
        console.error('Error parsing session:', e);
      }
    }
  
    // Step 2: Call backend to abandon current task (if any)
    if (sessionId) {
      try {
        const response = await apiClient.post(`/sessions/${sessionId}/abandon-task`);
        console.log('🔄 Task abandon response:', response.data);
        
        if (response.data.task_abandoned) {
          console.log(`✅ Task abandoned: ${response.data.task_type}`);
        } else {
          console.log('ℹ️ No active task to abandon');
        }
      } catch (error) {
        console.error('❌ Error abandoning task:', error);
        // Continue with reset even if abandon fails
      }
    }
  
    // Step 3: Reset UI state
    setMessages([{ sender: 'bot', text: 'Welcome to iResponse! How can I assist you?' }]);
    setNavigationContext({ level: 'main', categoryId: null, categoryTitle: null });
  
    try {
      const response = await apiClient.get('/suggestions/static_categories');
      setSuggestions(response.data.suggestions);
    } catch (error) {
      console.error('Error resetting chat:', error);
      setSuggestions([]);
    }
  
    setShowSuggestions(false);
  };

  // Send message function for chatbot
  const sendMessage = async (text, metadata = {}, clickedSuggestion = null) => {
    // Add interaction tracking
    const interaction_type = metadata.interaction_type || 'manual';
    const session_id = metadata.session_id || 'default';

    console.log("🔍 PHASE 1 DIAGNOSTIC - sendMessage called:");
    console.log(" - Text:", text);
    console.log(" - Interaction Type:", interaction_type);
    console.log(" - Session ID:", session_id);
    console.log(" - Navigation Context:", navigationContext);
    console.log(" - Timestamp:", new Date().toISOString());

    const userMessage = { sender: 'user', text };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await apiClient.post('/process_input_enhanced', {
        message: text,
        session_id: session_id,
        interaction_type: interaction_type,
        tool_name: metadata.tool_name || undefined,
        navigationContext: navigationContext,
        // Forward the clicked suggestion for backend augmentation
        selected_suggestion: clickedSuggestion,
        selectedSuggestion: clickedSuggestion,
        clicked_button: clickedSuggestion,
        button: clickedSuggestion
      });

      const data = response.data;
      const botText = data.response || 'Sorry, I could not process your request.';

      setMessages(prev => [...prev, {
        sender: 'bot',
        text: botText,
        debug: data.debug_info
      }]);

      // Smart suggestion handling based on backend directive
      if (data.ui_directive?.preserve_suggestions) {
        console.log('🎯 Preserving current suggestions as instructed by backend');
      } else if (data.suggestions && data.suggestions.suggestions) {
        setSuggestions(data.suggestions.suggestions);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        sender: 'bot',
        text: 'Error contacting server.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle suggestion clicks
  const handleSuggestionClick = async (suggestion) => {
    setLoadingSuggestion(suggestion.text);

    if (suggestion.action === 'show_subcategories') {
      // Update navigation context when entering subcategories
      setNavigationContext({
        level: 'subcategory',
        categoryId: suggestion.id,
        categoryTitle: suggestion.title || suggestion.text
      });

      try {
        const response = await apiClient.get(`/suggestions/subcategories/${suggestion.id}`);
        setSuggestions(response.data.suggestions || []);
      } catch (error) {
        console.error('Error loading subcategories:', error);
      }

    } else if (suggestion.action === 'trigger_nlp') {
      // Enhanced subcategory click with context preservation
      console.log("🔍 PHASE 1 DIAGNOSTIC - Quick action triggered:", suggestion.section);
      const text = suggestion.query || suggestion.section;
      await sendMessage(text, {
        interaction_type: 'quick_action',
        session_id: `quick_action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        quick_action_data: {
          section: suggestion.section,
          action: suggestion.action
        }
      }, suggestion);

    } else if (suggestion.action === 'go_back_to_static') {
      // Reset navigation context when going back
      setNavigationContext({
        level: 'main',
        categoryId: null,
        categoryTitle: null
      });

      try {
        const response = await apiClient.get('/suggestions/static_categories');
        setSuggestions(response.data.suggestions || []);
      } catch (error) {
        console.error('Error loading static categories:', error);
      }
    }

    setLoadingSuggestion(null);
  };

  return (
    <>
      <Router />
      <Chatbot
        visible={visible}
        shouldRender={shouldRender}
        messages={messages}
        suggestions={suggestions}
        showSuggestions={showSuggestions}
        loadingSuggestion={loadingSuggestion}
        isLoading={isLoading}
        chatRef={chatRef}
        toggleChat={toggleChat}
        toggleSuggestions={toggleSuggestions}
        sendMessage={sendMessage}
        handleSuggestionClick={handleSuggestionClick}
      />

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
}

export default App;